import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

// Account registration closes on 29 Sep 2026 23:59 IST
const currentHour = new Date().getHours();
const REGISTRATION_CLOSED = currentHour < 6 || currentHour >= 18;
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CinematicHero from "@/components/CinematicHero";
import IntroAnimation from "@/components/IntroAnimation";
import LogoBanner from "@/components/LogoBanner";
import AboutSection from "@/components/AboutSection";
import StrokeShowcase from "@/components/StrokeShowcase";
import SponsorCarousel from "@/components/SponsorCarousel";
import PinnedStory from "@/components/PinnedStory";
import FeeStructure from "@/components/FeeStructure";
import { Bubbles, LaneLine, Caustics } from "@/components/SwimmingFX";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { PARTICIPATING_COUNTRIES, Flag, getCountry3Letter } from "@/lib/countries";
import { INDIVIDUAL_EVENT_COUNT, RELAY_EVENT_COUNT } from "@/lib/brand";
import {
  ArrowRight, MapPin, Calendar, Waves as WavesIcon
} from "lucide-react";

const FAQS = [
  { q: "Who can participate in the championship?", a: "As per the latest guidelines issued by the Swimming Federation of India (SFI), only swimmers who have secured a Top 6 position in each respective individual event at the 22nd Masters Swimming National Championships held in Haryana are eligible to participate in the 2nd Asian Masters Swimming Championships 2026." },
  { q: "How is my age category calculated?", a: "Your Masters category is determined by your age as of December 31, 2026. Categories run in 5-year bands: 25–29, 30–34, … up to 80+. For relays, cumulative team age determines the relay category." },
  { q: "How many events can I register for?", a: "Each swimmer may enter a maximum of 5 individual events (chosen from 15 available) plus up to 2 relay events." },
  { q: "What are the entry fees?", a: "USD 60 registration fee per swimmer + USD 20 per individual event + USD 40 per relay team entry. For Indian athletes: ₹6000 registration fee + ₹2000 per individual event + ₹3000 per relay team entry (Late Registration Pricing). (Fees may be revised by the Organising Committee.)" },
  { q: "What documents do I need to submit?", a: "A valid passport copy and a medical fitness certificate are mandatory. Entry must be channelled through your National Federation." },
  { q: "What are the key deadlines?", a: "Final entries, accommodation, and accreditation deadline is 29th September 2026, 11:59 PM." },
  { q: "Is accommodation included?", a: "No — accommodation is booked separately through the official partner hotels at special championship tariffs. Airport transfers and venue shuttles are available for accredited participants." },
  { q: "Will airport transfers be arranged?", a: "Yes — accredited participants can request airport pickup/drop and venue shuttles from the Logistics module after registration." },
  { q: "What currencies are accepted?", a: "International cards (Visa, Mastercard, Amex), UPI, and Indian net-banking are supported." },
  { q: "What rules govern the competition?", a: "Competition is conducted under World Aquatics Masters Rules. Anti-doping rules apply to all participants." },
];

export default function Landing() {
  return (
    <div className="bg-[#0B1429] text-white">
      <IntroAnimation />
      <Navbar />

      {/* SPONSOR CAROUSEL (Scrolling under Navbar) */}
      <SponsorCarousel />

      {/* CINEMATIC HERO */}
      <CinematicHero />

      {/* OFFICIAL LOGO REVEAL */}
      <LogoBanner />

      {/* ABOUT — Event + SFI + KSA */}
      <AboutSection />

      {/* AUTO-CYCLING STROKE SHOWCASE (Nike-style) */}
      <StrokeShowcase />

      {/* STICKY SCROLL STORY */}
      <PinnedStory />

      {/* VENUE */}
      <section id="venue" className="py-16 md:py-24 relative overflow-x-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 grid md:grid-cols-2 gap-8 md:gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="min-w-0"
          >
            <h2 className="font-display font-black text-2xl sm:text-3xl md:text-5xl leading-tight mb-6">
              Centre for Sports Excellence
            </h2>
            <p className="text-[#CBD5E1] text-base md:text-lg leading-relaxed mb-6">
              Bengaluru's premier high-performance aquatic facility. Home of Dolphin Aquatics, situated inside the Centre for Sports Excellence — featuring a 50-meter Olympic-size competition pool, dedicated warm-up pools, fully automatic electronic timing (Omega), and 1,500 spectator seats.
            </p>
            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#00F0FF] mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <div className="text-white font-semibold">Venue</div>
                  <div className="text-[#94A3B8] break-words">Centre for Sports Excellence (Dolphin Aquatics), Bengaluru, Karnataka, India</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-[#00F0FF] mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <div className="text-white font-semibold">Dates</div>
                  <div className="text-[#94A3B8] break-words">October 4th – 10th, 2026</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <WavesIcon className="w-5 h-5 text-[#00F0FF] mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <div className="text-white font-semibold">Pools</div>
                  <div className="text-[#94A3B8] break-words">50m × 10-lane competition · 25m × 1 warm-up · 25–27°C</div>
                </div>
              </div>
            </div>
            <div className="mt-8"><LaneLine /></div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative h-[240px] sm:h-[320px] md:h-[420px] rounded-xl overflow-hidden border border-white/10 min-w-0"
          >
            <iframe
              title="Bengaluru venue map — Centre for Sports Excellence"
              src="https://www.google.com/maps?q=Centre+for+Sports+Excellence+Bangalore&output=embed"
              className="w-full h-full"
              loading="lazy"
              allowFullScreen
              style={{ filter: "invert(0.92) hue-rotate(180deg) brightness(0.9)" }}
            />
          </motion.div>
        </div>
      </section>

      {/* PARTICIPATING COUNTRIES */}
      <section id="countries" className="py-16 md:py-24 bg-[#142040] relative overflow-x-hidden">
        <Bubbles count={10} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 relative">
          <h2 className="font-display font-black text-3xl md:text-5xl mb-8 md:mb-12 max-w-2xl">{PARTICIPATING_COUNTRIES.length} nations. One <span className="text-[#00F0FF]">aquatic</span> family.</h2>
          <div className="flex flex-wrap gap-2 sm:gap-3">
            {PARTICIPATING_COUNTRIES.map((c, i) => (
              <motion.div
                key={c}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.03 }}
                data-testid={`country-${c}`}
                className="px-3 sm:px-4 py-2 min-h-[36px] bg-[#0B1429] border border-white/10 rounded-full text-sm text-white/85 hover:border-[#00F0FF]/60 hover:text-[#00F0FF] hover:-translate-y-0.5 transition-all flex items-center gap-2 cursor-default"
              >
                <Flag country={c} size={22} />
                <span className="hidden sm:inline">{c}</span>
                <span className="inline sm:hidden">{getCountry3Letter(c)}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FEE STRUCTURE */}
      <FeeStructure />

      {/* FAQ */}
      <section id="faq" className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10">
          <h2 className="font-display font-black text-4xl md:text-5xl mb-10">Everything you need to know.</h2>
          <Accordion type="single" collapsible className="space-y-3">
            {FAQS.map((f, i) => (
              <AccordionItem key={f.q} value={`q${i}`} data-testid={`faq-${i}`} className="bg-[#142040] border border-white/10 rounded-lg px-5">
                <AccordionTrigger className="text-left text-white hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-[#94A3B8]">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA — after fees/FAQ so the reassurance a visitor needs at the
          highest-stakes moment (price, eligibility, entry route) is already
          behind them, not below the button they're about to press. */}
      <section className="py-16 md:py-24 relative overflow-x-hidden">
        <Caustics opacity={0.1} />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 text-center relative">
          <div className="text-xs font-semibold text-[#94A3B8] mb-4">Be Part of History</div>
          <h2 className="font-display font-black text-4xl md:text-6xl mb-6 leading-tight">
            Your name on the <span className="text-[#00F0FF]">2026 podium</span>.<br />
            <span className="text-[#00F0FF]">United by Water.</span>
          </h2>
          <p className="text-lg text-[#CBD5E1] max-w-2xl mx-auto mb-10">
            · Entries end on 29th September 2026, 11:59 PM ·
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            {!REGISTRATION_CLOSED ? (
              <Link to="/register" data-testid="cta-register" className="btn-primary">Register Now <ArrowRight className="w-4 h-4" /></Link>
            ) : (
              <div data-testid="cta-register-closed" className="px-6 py-3 rounded-full bg-white/5 border border-white/20 text-white/50 text-sm font-semibold cursor-not-allowed">
                Registration Closed
              </div>
            )}
            <Link to="/login" data-testid="cta-login" className="btn-secondary">Athlete Login</Link>
          </div>
          <p className="mt-6 text-xs text-[#94A3B8]">
            USD 60 registration + USD 20 per event · No qualifying times required · Entries channelled through your National Federation
          </p>
          <div className="mt-12 flex justify-center"><LaneLine /></div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
